import { ReactElement, useMemo } from 'react';
import compact from 'lodash/compact';

import {
   DayOfWeek,
   EncounterMode,
   ReservationPreference,
   Status,
   TimeOfDay,
   WaitlistEntry,
   WaitlistEntryQuery,
} from '@dreryk/egabinet-waitlist-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { ServiceLocationId, ServiceLocationIdValue } from '@dreryk/egabinet-healthcare-provider-published-language-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';

import {
   createDynamicColumnsWidth,
   FilterPanel,
   IconFillColor,
   IconWrapper,
   MapMarkerIcon,
   Padding,
   TableColumnConfiguration,
   VideoConsultationIcon,
} from '@dreryk/react-components';
import { dateTimeMinuteFormatter, Translate } from '@dreryk/react-common';
import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Slot } from '@dreryk/watermelon';

import { PatientTableCellPresenter } from '~libs.desktop.shared.patient';
import { protectFeature } from '~libs.shared.system';
import { toHealthcareProviderIdentifier } from '~libs.shared.healthcare-provider-location';

import { translationKeys } from '../translations';
import { WaitlistFilterPanelSearchParam } from '../filters/WaitlistFilterPanel.types';
import { encounterModesDeserializer } from '../filters/controllers/encounterModes/EncounterModesController.utils';
import { reservationPreferenceDeserializer } from '../filters/controllers/reservationPreference/ReservationPreferenceController.utils';
import { statusDeserializer } from '../filters/controllers/status/StatusController.utils';
import { urgentDeserializer } from '../filters/controllers/urgent/UrgentController.utils';
import { getTimeOfDayByInternalId } from '../filters/controllers/patientAvailability/PatientAvailabilityController.utils';
import { NO_SERVICE_ITEM } from '../filters/controllers/servicesController/ServicesController';

import { ServiceProvidingLocationCell } from './cells/ServiceProvidingLocationCell/ServiceProvidingLocationCell';
import { NoteCell } from './cells/NoteCell';
import { StatusCell } from './cells/StatusCell';
import { AdditionalOptionsCell } from './cells/AdditionalOptionsCell/AdditionalOptionsCell';
import { OptionsCell } from './cells/OptionsCell/OptionsCell';
import { UrgentCell } from './cells/UrgentCell';
import { PatientAvailabilityPreferenceCell } from './cells/PatientAvailabilityPreferenceCell/PatientAvailabilityPreferenceCell';
import { PractitionerCell } from './cells/PractitionerCell/PractitionerCell';

// eslint-disable-next-line max-lines-per-function
export const useWaitlistTableColumnConfiguration = (): TableColumnConfiguration<WaitlistEntry>[] => {
   const columnsWidth = useMemo(() => {
      return createDynamicColumnsWidth({
         creationDate: '140px',
         urgent: '24px',
         patient: protectFeature('searchingPrivateMedicalServices', '20%', '25%'),
         note: '24px',
         specialist: protectFeature('searchingPrivateMedicalServices', '20%', '25%'),
         service: protectFeature('searchingPrivateMedicalServices', '20%', '0px'),
         patientAvailability: protectFeature('searchingPrivateMedicalServices', '20%', '25%'),
         serviceProvidingLocation: protectFeature('searchingPrivateMedicalServices', '20%', '25%'),
         status: '154px',
         options: '130px',
         encounterMode: protectFeature('bookingVideoConsultations', '32px', '0px'),
         additionalOptions: '24px',
      });
   }, []);
   return useMemo(
      // eslint-disable-next-line max-lines-per-function
      () =>
         compact([
            {
               name: 'creationDate',
               label: translationKeys.dateTime,
               accessor: 'creationDate',
               width: columnsWidth.creationDate,
               formatter: dateTimeMinuteFormatter,
               sortingDisabled: true,
            },
            {
               name: 'urgent',
               accessor: 'importance',
               width: columnsWidth.urgent,
               sortingDisabled: true,
               padding: Padding.NONE,
               renderer: ({ bean }) => {
                  return <UrgentCell urgent={bean.importance === 'IMPORTANT'} />;
               },
            },
            {
               name: 'patient',
               label: translationKeys.patient,
               accessor: 'patient',
               width: columnsWidth.patient,
               renderer: (props) => {
                  return (
                     <PatientTableCellPresenter
                        patient={props.bean.patient}
                        goBackLinkLabel={translationKeys.goBackToWaitlist}
                     />
                  );
               },
               sortingDisabled: true,
            },
            {
               name: 'note',
               accessor: 'note',
               width: columnsWidth.note,
               renderer: (props) => {
                  return props.bean.note ? <NoteCell note={props.bean.note} /> : null;
               },
               sortingDisabled: true,
               padding: Padding.NONE,
            },
            {
               name: 'practitioner',
               label: translationKeys.specialist,
               accessor: 'practitioner',
               width: columnsWidth.specialist,
               renderer: (props) => {
                  return (
                     <PractitionerCell
                        practitioner={props.bean.practitioner}
                        important={props.bean.reservationPreference === 'PRACTITIONER'}
                     />
                  );
               },
               sortingDisabled: true,
               padding: Padding.NONE,
            },
            protectFeature('searchingPrivateMedicalServices', {
               name: 'service',
               label: translationKeys.service,
               accessor: 'receptionService',
               width: columnsWidth.service,
               renderer: (props): ReactElement | null => {
                  return props.bean.receptionService ? (
                     <>{props.bean.receptionService.name}</>
                  ) : (
                     <Translate>{translationKeys.notSelected}</Translate>
                  );
               },
               sortingDisabled: true,
            }),
            {
               name: 'patientAvailability',
               label: translationKeys.preferredDateAbbreviated,
               accessor: 'patientAvailability',
               width: columnsWidth.patientAvailability,
               renderer: (props): ReactElement => {
                  return (
                     <PatientAvailabilityPreferenceCell
                        patientAvailability={props.bean.patientAvailability}
                        important={props.bean.reservationPreference === 'TIME'}
                     />
                  );
               },
               sortingDisabled: true,
               padding: Padding.NONE,
            },
            {
               name: 'serviceProvidingLocation',
               label: translationKeys.serviceProvidingLocationAbbreviation,
               accessor: 'location',
               width: columnsWidth.serviceProvidingLocation,
               renderer: (props) => {
                  return (
                     <ServiceProvidingLocationCell
                        location={props.bean.location}
                        videoConsultation={props.bean.encounterMode === 'VIDEO_CONSULTATION'}
                        important={props.bean.reservationPreference === 'LOCATION'}
                     />
                  );
               },
               sortingDisabled: true,
               padding: Padding.NONE,
            },
            {
               name: 'status',
               label: translationKeys.status,
               accessor: 'status',
               width: columnsWidth.status,
               renderer: (props) => {
                  return <StatusCell status={props.bean.status} />;
               },
               sortingDisabled: true,
            },
            {
               name: 'options',
               label: translationKeys.options,
               accessor: 'id',
               width: columnsWidth.options,
               renderer: (props) => {
                  return <OptionsCell waitlistEntry={props.bean} />;
               },
               sortingDisabled: true,
            },
            protectFeature('bookingVideoConsultations', {
               name: 'encounterMode',
               accessor: 'encounterMode',
               width: columnsWidth.encounterMode,
               renderer: (props) => {
                  return (
                     <IconWrapper testId={'encounterMode'} fillColor={IconFillColor.BACKGROUND}>
                        {props.bean.encounterMode === 'VIDEO_CONSULTATION' ? (
                           <VideoConsultationIcon />
                        ) : (
                           <MapMarkerIcon />
                        )}
                     </IconWrapper>
                  );
               },
               sortingDisabled: true,
               padding: Padding.NONE,
            }),
            {
               name: 'additionalOptions',
               accessor: 'id',
               width: columnsWidth.additionalOptions,
               renderer: (props) => {
                  return <AdditionalOptionsCell waitlistEntry={props.bean} />;
               },
               sortingDisabled: true,
               padding: Padding.NONE,
            },
         ]),
      [
         columnsWidth.creationDate,
         columnsWidth.urgent,
         columnsWidth.patient,
         columnsWidth.note,
         columnsWidth.specialist,
         columnsWidth.service,
         columnsWidth.patientAvailability,
         columnsWidth.serviceProvidingLocation,
         columnsWidth.status,
         columnsWidth.options,
         columnsWidth.encounterMode,
         columnsWidth.additionalOptions,
      ]
   );
};

type WaitlistPanelDeserializedParamsMap = {
   [WaitlistFilterPanelSearchParam.PRACTITIONER]: 'practitioners';
   [WaitlistFilterPanelSearchParam.HEALTHCARE_PROVIDER_LOCATION]: 'healthcareProviderLocations';
   [WaitlistFilterPanelSearchParam.RESERVATION_PREFERENCE]: 'reservationPreference';
   [WaitlistFilterPanelSearchParam.STATUS]: 'statuses';
   [WaitlistFilterPanelSearchParam.URGENT]: 'urgent';
   [WaitlistFilterPanelSearchParam.ENCOUNTER_MODE]: 'encounterModes';
   [WaitlistFilterPanelSearchParam.SERVICES]: 'services';
   [WaitlistFilterPanelSearchParam.PATIENT_AVAILABILITY_DAY_OF_WEEK]: 'patientAvailabilityDayOfWeek';
   [WaitlistFilterPanelSearchParam.PATIENT_AVAILABILITY_TIMES_OF_DAY]: 'patientAvailabilityTimesOfDay';
};

type WaitlistPanelDeserializedFilters = {
   practitioners: PractitionerId[];
   healthcareProviderLocations: ServiceLocationId[];
   reservationPreference: ReservationPreference | null;
   statuses: Status[];
   urgent: boolean;
   encounterModes: EncounterMode[];
   services: (ReceptionServiceId | null)[];
   patientAvailabilityDayOfWeek: DayOfWeek | null;
   patientAvailabilityTimesOfDay: TimeOfDay[];
};

/* eslint-disable max-lines */
// eslint-disable-next-line max-lines-per-function
export const useWaitlistEntriesTableLazyLoadedDataConfigurationByCqrsQuery = (
   patientSearchText: string | null
): LazyLoadedDataConfigurationByCqrsQuery<WaitlistEntry> => {
   const deserializedFilters = FilterPanel.useDeserializedFilters<
      WaitlistPanelDeserializedFilters,
      WaitlistPanelDeserializedParamsMap
   >({
      [WaitlistFilterPanelSearchParam.PRACTITIONER]: (currentParamValue) => ({
         practitioners: FilterPanel.paramToNumberArray(currentParamValue).map(PractitionerId.of),
      }),
      [WaitlistFilterPanelSearchParam.HEALTHCARE_PROVIDER_LOCATION]: (currentParamValue) => ({
         healthcareProviderLocations: FilterPanel.paramToArray<ServiceLocationId, 'string'>(
            currentParamValue,
            'string',
            (value) => {
               const healthcareProviderIdentifier = toHealthcareProviderIdentifier(
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                  value as ServiceLocationIdValue
               );
               return ServiceLocationId.of(healthcareProviderIdentifier.type, healthcareProviderIdentifier.id);
            }
         ),
      }),
      [WaitlistFilterPanelSearchParam.RESERVATION_PREFERENCE]: (currentParamValue) => ({
         reservationPreference: reservationPreferenceDeserializer(currentParamValue),
      }),
      [WaitlistFilterPanelSearchParam.STATUS]: (currentParamValue) => ({
         statuses: currentParamValue ? statusDeserializer(currentParamValue) : [],
      }),
      [WaitlistFilterPanelSearchParam.URGENT]: (currentParamValue) => ({
         urgent: currentParamValue ? urgentDeserializer(currentParamValue) : false,
      }),
      [WaitlistFilterPanelSearchParam.ENCOUNTER_MODE]: (currentParamValue) => ({
         encounterModes: encounterModesDeserializer(currentParamValue),
      }),
      [WaitlistFilterPanelSearchParam.SERVICES]: (currentParamValue) => ({
         services: FilterPanel.paramToArray(currentParamValue, 'string', (mappedParamValue) => {
            if (mappedParamValue === NO_SERVICE_ITEM) {
               return null;
            }
            return ReceptionServiceId.of(Number(mappedParamValue));
         }),
      }),
      [WaitlistFilterPanelSearchParam.PATIENT_AVAILABILITY_DAY_OF_WEEK]: (currentParamValue) => ({
         patientAvailabilityDayOfWeek: FilterPanel.paramToSingleValue(
            currentParamValue,
            'string',
            (dayOfWeek) => (dayOfWeek || null) as DayOfWeek | null
         ),
      }),
      [WaitlistFilterPanelSearchParam.PATIENT_AVAILABILITY_TIMES_OF_DAY]: (currentParamValue) => ({
         patientAvailabilityTimesOfDay: FilterPanel.paramToNumberArray(currentParamValue)
            .map(getTimeOfDayByInternalId)
            .filter(Boolean) as TimeOfDay[],
      }),
   });

   return useLazyLoadedDataConfigurationByCqrsQuery(() => {
      const query = new WaitlistEntryQuery();
      query.practitionerIds = Slot.emptyIfNull(
         deserializedFilters.practitioners.length > 0 ? deserializedFilters.practitioners : null
      );
      query.serviceLocationIds = Slot.emptyIfNull(
         deserializedFilters.healthcareProviderLocations.length > 0
            ? deserializedFilters.healthcareProviderLocations
            : null
      );
      query.reservationPreference = Slot.emptyIfNull(deserializedFilters.reservationPreference);
      query.statuses = Slot.emptyIfNull(deserializedFilters.statuses.length > 0 ? deserializedFilters.statuses : null);
      query.importance = Slot.emptyIfNull(deserializedFilters.urgent ? 'IMPORTANT' : null);
      query.encounterModes = Slot.emptyIfNull(
         deserializedFilters.encounterModes.length > 0 ? deserializedFilters.encounterModes : null
      );
      query.receptionServiceIds = (Slot.emptyIfNull(
         deserializedFilters.services.length > 0 ? deserializedFilters.services : null
         // TODO: DREGAB-26860 Naprawić typescript-generator https://dreryk.atlassian.net/browse/DREGAB-26860
      ) as unknown) as Slot<ReceptionServiceId[]>;
      query.freeformCriteria = Slot.emptyIfNull(patientSearchText);
      query.patientAvailabilityDay = Slot.emptyIfNull(deserializedFilters.patientAvailabilityDayOfWeek);
      query.patientAvailabilityTimes = Slot.emptyIfNull(
         deserializedFilters.patientAvailabilityTimesOfDay.length > 0
            ? deserializedFilters.patientAvailabilityTimesOfDay
            : null
      );
      return query;
   });
};
